// src/lib/lead/actions/extractionOrchestrator.ts
import type { Lead, ExtractedLeadV2 } from "@/types/lead";
import { createExtractScheduler } from "@/lib/lead/extractionScheduler";
import { runExtractionOnce } from "@/lib/lead/extraction/runExtraction";
import type { TextHeuristics } from "@/lib/lead/extraction/textHeuristics";
import type { LeadDefaultFollowUpPolicy } from "@/lib/lead/extraction/leadDefaultFollowUpPolicy";
import type { MergeLeadResult } from "@/lib/lead/extraction/mergeLeadFromTranscript";

type ExtractFn = (text: string) => Promise<ExtractedLeadV2>;

type SetLeads = (updater: (prev: Lead[]) => Lead[]) => void;

export type ExtractionOrchestratorDeps = {
  extract: ExtractFn;
  heuristics: TextHeuristics;
  getLeadDefaultFollowUpPolicy?: () => LeadDefaultFollowUpPolicy;

  getNextRequestId: () => number;
  isLatestRequestId: (id: number) => boolean;
  getCurrentLead: () => Lead | null;

  setCurrentLead: (l: Lead) => void;
  setLeads: SetLeads;

  // Optional: keep a ref in sync
  setCurrentLeadRef?: (l: Lead | null) => void;

  /**
   * Phase 2 of transcript merge v2: forward conflicts up to the shell so it
   * can open `<LeadMergeConflictModal>`.
   */
  onMergeConflicts?: (args: { leadId: string; mergeResult: MergeLeadResult; source: "extract" }) => void;
};

export function createLeadExtractionOrchestrator(
  deps: ExtractionOrchestratorDeps,
  cfg?: { debounceMs?: number }
) {
  const runNow = async (leadId: string, transcript: string) => {
    const updatedLead = await runExtractionOnce(
      leadId,
      transcript,
      {
        extract: deps.extract,
        heuristics: deps.heuristics,
        getLeadDefaultFollowUpPolicy: deps.getLeadDefaultFollowUpPolicy,
        onMergeConflicts: deps.onMergeConflicts,
      },
      {
        getNextRequestId: deps.getNextRequestId,
        isLatestRequestId: deps.isLatestRequestId,
        getCurrentLead: deps.getCurrentLead,
      }
    );

    if (!updatedLead) return;

    deps.setCurrentLead(updatedLead);
    deps.setCurrentLeadRef?.(updatedLead);

    deps.setLeads((prev) => prev.map((l) => (l.id === leadId ? updatedLead : l)));
  };

  const scheduler = createExtractScheduler({
    runExtractionNow: runNow,
    debounceMs: cfg?.debounceMs ?? 800,
  });

  return {
    runNow,
    schedule: scheduler.schedule,
    flush: scheduler.flush,
    cancel: scheduler.cancel,
    getInFlight: scheduler.getInFlight,
  };
}